import type { ReactNode, ElementType, HTMLAttributes } from "react";

/* Reusable presentation primitives for the Tables and Bridges design system.
   Compose these instead of re-declaring container widths, section padding,
   or heading rhythm on every page. */

type Tone = "navy" | "warm-white" | "deep-green" | "ink";

const toneBg: Record<Tone, string> = {
  navy: "bg-navy text-warm-white",
  "warm-white": "bg-warm-white text-navy",
  "deep-green": "bg-deep-green text-warm-white",
  ink: "bg-ink text-warm-white",
};

function cx(...parts: Array<string | undefined | false>) {
  return parts.filter(Boolean).join(" ");
}

/* ------------------------ Container ------------------------ */
export function Container({
  as: Tag = "div",
  className,
  children,
  ...rest
}: { as?: ElementType; children: ReactNode } & HTMLAttributes<HTMLElement>) {
  return (
    <Tag className={cx("container-editorial", className)} {...rest}>
      {children}
    </Tag>
  );
}

/* ------------------------- Section ------------------------- */
export function Section({
  tone = "navy",
  as: Tag = "section",
  className,
  children,
  id,
}: {
  tone?: Tone;
  as?: ElementType;
  className?: string;
  children: ReactNode;
  id?: string;
}) {
  return (
    <Tag id={id} className={cx(toneBg[tone], "section-y", className)}>
      <div className="container-editorial">{children}</div>
    </Tag>
  );
}

/* ---------------------- SectionHeading --------------------- */
export function SectionHeading({
  eyebrow,
  title,
  lede,
  align = "left",
  as: Tag = "h2",
  className,
}: {
  eyebrow?: string;
  title: ReactNode;
  lede?: ReactNode;
  align?: "left" | "center";
  as?: "h1" | "h2" | "h3";
  className?: string;
}) {
  return (
    <header
      className={cx(
        "max-w-3xl",
        align === "center" && "mx-auto text-center",
        className,
      )}
    >
      {eyebrow && <p className="eyebrow mb-5">{eyebrow}</p>}
      <Tag className="font-display">{title}</Tag>
      {lede && (
        <p
          className={cx(
            "mt-6 text-base md:text-lg leading-relaxed text-current/80 measure-body",
            align === "center" && "mx-auto",
          )}
        >
          {lede}
        </p>
      )}
      <span aria-hidden className={cx("divider-gold mt-8", align === "center" && "mx-auto")} />
    </header>
  );
}

/* --------------------------- CTA --------------------------- */
export function CTASection({
  eyebrow,
  title,
  lede,
  primary,
  secondary,
  tone = "deep-green",
}: {
  eyebrow?: string;
  title: ReactNode;
  lede?: ReactNode;
  primary?: { label: string; href: string; external?: boolean };
  secondary?: { label: string; href: string; external?: boolean };
  tone?: "deep-green" | "navy" | "ink";
}) {
  return (
    <Section tone={tone}>
      <div className="grid gap-10 lg:grid-cols-[1.4fr_auto] lg:items-end">
        <div>
          {eyebrow && <p className="eyebrow mb-5">{eyebrow}</p>}
          <h2 className="font-display max-w-2xl">{title}</h2>
          {lede && (
            <p className="mt-6 measure-body text-warm-white/80 text-base md:text-lg leading-relaxed">
              {lede}
            </p>
          )}
        </div>
        {(primary || secondary) && (
          <div className="flex flex-wrap gap-3 lg:justify-end">
            {primary && (
              <a
                href={primary.href}
                target={primary.external ? "_blank" : undefined}
                rel={primary.external ? "noopener noreferrer" : undefined}
                className="btn-base btn-primary"
              >
                {primary.label}
              </a>
            )}
            {secondary && (
              <a
                href={secondary.href}
                target={secondary.external ? "_blank" : undefined}
                rel={secondary.external ? "noopener noreferrer" : undefined}
                className="btn-base btn-secondary-dark"
              >
                {secondary.label}
              </a>
            )}
          </div>
        )}
      </div>
    </Section>
  );
}

export function TestimonialBlock({
  quote,
  attribution,
  role,
  tone = "warm-white",
}: {
  quote: ReactNode;
  attribution: string;
  role?: string;
  tone?: "warm-white" | "navy" | "deep-green";
}) {
  const isLight = tone === "warm-white";
  return (
    <figure
      className={cx(
        "relative border-l-2 border-gold pl-6 md:pl-10 py-4",
        isLight ? "text-navy" : "text-warm-white",
      )}
    >
      <blockquote className="font-display text-2xl md:text-3xl leading-snug measure-body">
        &ldquo;{quote}&rdquo;
      </blockquote>
      <figcaption className="mt-6 text-xs uppercase tracking-[0.2em]">
        <span className="text-gold font-semibold">{attribution}</span>
        {role && <span className="opacity-70"> - {role}</span>}
      </figcaption>
    </figure>
  );
}


